import type { FutebolEventoPartida, FutebolPartidaAtiva } from '@/shared/types';

export type EstadoJogoMobile = 'idle' | 'pre_jogo' | 'ao_vivo' | 'encerrado';

export function resolverEstadoJogoMobile(
  partida: FutebolPartidaAtiva | null,
): EstadoJogoMobile {
  if (!partida?.fixture_id) return 'idle';
  if (partida.tempo_jogo === 'FT') return 'encerrado';
  const tempo = partida.tempo_jogo;
  if (tempo === 'HT' || tempo === 'ET' || tempo === 'PEN') return 'ao_vivo';
  if (tempo && /^\d+$/.test(tempo)) return 'ao_vivo';
  return 'pre_jogo';
}

const PALAVRAS_IGNORADAS = new Set(['de', 'da', 'do', 'das', 'dos', 'fc', 'sc', 'ec', 'ac', 'cf']);

/**
 * Retorna true quando o último evento é um CRAQUE_BAGRE ainda não expirado,
 * indicando que a partida está no intervalo (HT).
 */
export function isIntervaloAtivo(ultimoEvento: FutebolEventoPartida): boolean {
  const ev = ultimoEvento as { tipo?: string; expiracao?: string } | null | undefined;
  if (!ev || ev.tipo !== 'CRAQUE_BAGRE') return false;
  return ev.expiracao ? new Date(ev.expiracao) > new Date() : false;
}

/** Sigla de 3 letras para o placar estilo broadcast (ex.: "Portugal" → "POR"). */
export function siglaTime(nome: string): string {
  const cleaned = nome
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .trim();
  if (!cleaned) return '—';

  const partes = cleaned
    .split(/\s+/)
    .filter(p => !PALAVRAS_IGNORADAS.has(p.toLowerCase()));

  if (partes.length === 0) return cleaned.slice(0, 3).toUpperCase();
  if (partes.length === 1) return partes[0].slice(0, 3).toUpperCase();
  return partes.slice(0, 3).map(p => p[0] ?? '').join('').toUpperCase();
}

/**
 * Formata tempo de jogo para o bloco branco do placar broadcast.
 * `segundos` (0-59) é uma estimativa client-side dentro do minuto atual —
 * ver `useSegundosEstimados`. Ignorado para tempos especiais (HT/FT/ET/PEN).
 */
export function formatarTempoPlacarTv(tempo: string | null | undefined, segundos = 0): string {
  if (!tempo) return '--:--';
  if (tempo === 'HT') return 'INT';
  if (tempo === 'FT') return 'FIM';
  if (tempo === 'ET') return 'PRG';
  if (tempo === 'PEN') return 'PEN';
  if (/^\d+$/.test(tempo)) {
    const min = parseInt(tempo, 10);
    const seg = Math.min(59, Math.max(0, Math.round(segundos)));
    return `${String(min).padStart(2, '0')}:${String(seg).padStart(2, '0')}`;
  }
  return tempo;
}

/**
 * URL do logo da liga na BSD. `null` quando não há `liga_id` (jogo sincronizado
 * antes desse campo existir, ou provedor sem esse dado) — nesse caso usar
 * `siglaLiga` como fallback de texto.
 */
export function ligaLogoUrl(ligaId: number | null | undefined): string | null {
  if (ligaId == null) return null;
  return `https://sports.bzzoiro.com/img/league/${ligaId}/?bg=transparent`;
}

/** Abrevia nome da liga para o emblema central. */
export function siglaLiga(nome: string | null | undefined): string {
  if (!nome) return 'VS';
  const sigla = siglaTime(nome.replace(/serie|série|league|copa|campeonato/gi, '').trim() || nome);
  return sigla.slice(0, 3) || 'VS';
}

const TEMPOS_ESPECIAIS = new Set(['HT', 'FT', 'ET', 'PEN']);

/**
 * Identifica um gol de forma estável: lado + placar do time após o gol.
 * Imune a divergência de minuto entre WSS e REST.
 */
export function chaveGolDedup(
  time: 'casa' | 'visitante',
  placarCasa: number,
  placarVisitante: number,
): string {
  const score = time === 'casa' ? placarCasa : placarVisitante;
  return `gol-${time}-${score}`;
}

/** True se o placar do time que marcou avancou — evita overlay duplo quando o evento chega antes do score. */
export function placarDoTimeAvancou(
  time: 'casa' | 'visitante',
  prevCasa: number,
  prevVisitante: number,
  novoCasa: number,
  novoVisitante: number,
): boolean {
  return time === 'casa' ? novoCasa > prevCasa : novoVisitante > prevVisitante;
}

/** Evita regressão do cronômetro quando writers concorrentes enviam minutos defasados. */
export function preservarTempoJogo(
  atual: string | null | undefined,
  novo: string | null | undefined,
): string | null {
  if (!novo) return atual ?? null;
  if (!atual) return novo;

  if (TEMPOS_ESPECIAIS.has(novo)) return novo;

  if (TEMPOS_ESPECIAIS.has(atual)) {
    if (novo === 'FT' || /^\d+$/.test(novo)) return novo;
    return atual;
  }

  const atualNum = /^\d+$/.test(atual) ? parseInt(atual, 10) : null;
  const novoNum  = /^\d+$/.test(novo)  ? parseInt(novo, 10)  : null;

  if (atualNum != null && novoNum != null) {
    return String(Math.max(atualNum, novoNum));
  }

  return novo;
}
